import React from 'react';
import {
    FlexDiv,
    Heading2,
    Heading3,
    TextDefault,
    TextTiny,
    ThemedButton,
    StatusBadge,
    palette,
} from '@awarego-ui/components';

const DANGER_COLOR = {
    Critical: palette.vibrant.raspberry,
    High: palette.vibrant.red,
    Medium: palette.vibrant.gold,
};

function AttackCard({ email, idx }) {
    const color = DANGER_COLOR[email.danger_level] || DANGER_COLOR.High;
    return (
        <FlexDiv
            column
            gap={12}
            padding={20}
            borderRadius={12}
            mb={16}
            backgroundColor={palette.graphite.white}
            style={{
                border: `1px solid ${palette.graphite.gray}`,
            }}
        >
            <FlexDiv
                alignCenter
                gap={12}
                style={{
                    justifyContent: 'space-between',
                }}
            >
                <TextTiny
                    color={palette.graphite.charcoal}
                    style={{
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                    }}
                >
                    {email.attack_type || `Attack #${idx + 1}`}
                </TextTiny>
                <StatusBadge
                    text={`${email.danger_level || 'High'} risk`}
                    color={color}
                />
            </FlexDiv>
            <FlexDiv column gap={4}>
                <TextTiny
                    color={palette.graphite.charcoal}
                    style={{ display: 'block' }}
                >
                    <strong>From: </strong>
                    {email.from}
                </TextTiny>
                <TextTiny
                    color={palette.graphite.charcoal}
                    style={{ display: 'block' }}
                >
                    <strong>Subject: </strong>
                    <span style={{ color: palette.graphite.onyx }}>
                        {email.subject}
                    </span>
                </TextTiny>
            </FlexDiv>
            <FlexDiv
                padding={14}
                borderRadius={8}
                backgroundColor={palette.evergreen.mintLight}
                style={{
                    fontSize: 14,
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap',
                    color: palette.graphite.heavyMetal,
                    width: '100%',
                }}
            >
                {email.body}
            </FlexDiv>
            {email.why_it_works && (
                <FlexDiv
                    padding={12}
                    borderRadius={8}
                    backgroundColor={palette.pastel.red}
                    style={{
                        borderLeft: `3px solid ${color}`,
                        width: '100%',
                    }}
                >
                    <TextTiny color={palette.vibrant.raspberry}>
                        <strong>Why this works:</strong> {email.why_it_works}
                    </TextTiny>
                </FlexDiv>
            )}
        </FlexDiv>
    );
}

export default function AwareExposeAttacks({ emails, canRerun, onRerun }) {
    return (
        <div style={{ width: '100%' }}>
            <FlexDiv column gap={6} mb={24}>
                <TextTiny
                    color={palette.evergreen.aspargus}
                    style={{
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                        display: 'block',
                    }}
                >
                    Step 2 of 2 — Your personalised attacks
                </TextTiny>
                <Heading2 displayBlock>
                    Three attacks crafted against you
                </Heading2>
                <TextDefault color={palette.graphite.charcoal}>
                    These are educational examples. Each one is grounded in the
                    public signals discovered for you, and explains the
                    manipulation pattern attackers would use. Treat every real
                    email with the same scepticism.
                </TextDefault>
            </FlexDiv>

            {emails.map((email, idx) => (
                <AttackCard key={email.id || idx} email={email} idx={idx} />
            ))}

            <FlexDiv
                column
                gap={4}
                mt={16}
                padding={20}
                borderRadius={12}
                backgroundColor={palette.evergreen.mintLight}
                style={{ border: `1px solid ${palette.evergreen.mint}` }}
            >
                <Heading3 displayBlock>Recognise the patterns</Heading3>
                <TextTiny
                    color={palette.graphite.heavyMetal}
                    style={{ display: 'block' }}
                >
                    These attacks were generated in seconds using only public
                    information. Train your team to recognise the patterns
                    before real attackers strike.
                </TextTiny>
            </FlexDiv>

            {canRerun && (
                <FlexDiv mt={24} gap={12}>
                    <ThemedButton
                        mode="secondary"
                        text="Run AwareExpose again"
                        onClick={onRerun}
                    />
                </FlexDiv>
            )}
        </div>
    );
}
